/*
  Image load-in directive `v-img-fade` (HAP-1890, parent HAP-1884 Phase 2.2).
  Card media on the main pages is painted as a CSS `background-image` on a
  fixed-size div — so it "pops" in the instant the bytes arrive. This directive
  makes that media gently fade (optionally blur-up) into place once the image
  has actually decoded, instead of snapping in.

  Usage (the value is the image URL the element is showing):
    <div :style="`background-image: url(${img})`" v-img-fade="img" />        // fade
    <div :style="`background-image: url(${img})`" v-img-fade:blur="img" />   // blur-up
    <img :src="img" v-img-fade="img" />                                      // works on <img> too

  Why a directive (not a CSS class or @vueuse/motion):
    • The reveal has to wait for the REAL load event of whatever URL the element
      is painting — background-image divs have no load event of their own, so we
      preload the same URL (the browser dedupes the request) and fade on its load.
    • WAAPI animates opacity/filter on the element's own timeline and clears the
      inline style on finish, so it never clobbers a card's hover transform/CSS
      (same hand-back-to-CSS approach as v-press / v-reveal).
    • Reduced motion must be a true no-op: the media has to show IMMEDIATELY and
      fully — the global CSS guard only collapses durations, it would still run
      the fade — so we gate in JS and never hide the element at all.

  Compositor-only (opacity, plus filter for blur-up) and the element keeps its
  fixed size throughout, so there is never any layout shift.
*/
import { DURATION, EASING } from '../helpers/motion'

const BLUR = 12 // px the image blurs up from when the :blur modifier is used

// Live reduced-motion check — JS-driven motion neutralizes itself the same way
// the CSS guard does (memory: HAP-1886). Under reduced motion this directive is
// a complete no-op: nothing is hidden, nothing animates.
const reducedMotionMQ =
	typeof window !== 'undefined' && typeof window.matchMedia === 'function'
		? window.matchMedia('(prefers-reduced-motion: reduce)')
		: { matches: false }

function url(binding) {
	return typeof binding.value === 'string' ? binding.value.trim() : ''
}

// Drop our inline pre-hide / WAAPI hold and hand the element back to CSS.
function release(el) {
	el.style.opacity = ''
	el.style.filter = ''
	el.style.willChange = ''
}

function fadeIn(el, withBlur) {
	const frames = withBlur
		? [
				{ opacity: 0, filter: `blur(${BLUR}px)` },
				{ opacity: 1, filter: 'blur(0px)' },
		  ]
		: [{ opacity: 0 }, { opacity: 1 }]
	// fill:'both' holds the hidden start frame so there's no flash before the fade,
	// and pins the end until we cancel + clear on finish.
	const anim = el.animate(frames, { duration: DURATION.base, easing: EASING.decel, fill: 'both' })
	el.__imgFadeAnim = anim
	anim.onfinish = () => {
		if (el.__imgFadeAnim !== anim) return
		anim.cancel()
		release(el)
		el.__imgFadeAnim = null
	}
}

function run(el, binding) {
	// Reduced motion: show immediately, never hide.
	if (reducedMotionMQ.matches) return
	const src = url(binding)
	// No image (placeholder / map state) → nothing to fade; show as-is.
	if (!src) return
	// Same URL we already handled — don't re-fade on an unrelated re-render.
	if (el.__imgFadeUrl === src) return

	// New URL: cancel any in-flight fade and pre-hide before the next paint.
	if (el.__imgFadeAnim) el.__imgFadeAnim.cancel()
	el.__imgFadeUrl = src
	el.__imgFadeStarted = false
	const withBlur = binding.modifiers.blur === true
	el.style.opacity = '0'
	el.style.willChange = withBlur ? 'opacity, filter' : 'opacity'
	if (withBlur) el.style.filter = `blur(${BLUR}px)`

	const start = () => {
		if (el.__imgFadeUrl !== src || el.__imgFadeStarted) return
		el.__imgFadeStarted = true
		fadeIn(el, withBlur)
	}

	const img = new Image()
	img.onload = start
	// On error don't strand the element hidden — just reveal it.
	img.onerror = () => {
		if (el.__imgFadeUrl !== src || el.__imgFadeStarted) return
		el.__imgFadeStarted = true
		release(el)
	}
	img.src = src
	// Already in the browser cache: decoded synchronously, onload may not fire.
	if (img.complete && img.naturalWidth > 0) start()
}

export const imgFadeDirective = {
	mounted(el, binding) {
		run(el, binding)
	},
	// The bound URL is reactive (lists re-render with new data) — re-fade when it changes.
	updated(el, binding) {
		run(el, binding)
	},
	unmounted(el) {
		if (el.__imgFadeAnim) el.__imgFadeAnim.cancel()
		delete el.__imgFadeAnim
		delete el.__imgFadeUrl
		delete el.__imgFadeStarted
	},
}

export default imgFadeDirective
